import * as CL from '@design-system/component-library';
import { DialogWrapper } from '../DialogWrapper';
import { Price, getSaving } from './SubscriptionUtils';
import { addMsg, continueShoppingMsg, orderMsg, quantityMsg, removeMsg, subtractMsg, t } from '../../common/i18n';
import { dsClass } from '../../common/constants/dsClasses';
import { useAuth } from '../../public/site/AuthProvider';
import { useState } from 'react';
import type { OnlineModel } from '../../generated/api/onlineModel';
import type { OrderProps, SalesProductsOrderProps, SubscriptionCardWithModel } from './types';
import type { SubscriptionCardOffer } from '../SubscriptionCard/SubscriptionCard';

import './SubscriptionModal.scss';

export interface ModalDescriptionItem {
  title?: string;
  content?: JSX.Element;
}

export interface SubscriptionCard2ModalProps {
  header: string;
  descriptionItems: ModalDescriptionItem[];
  onNumberSelectionChange: (newOrKeepNumber: string) => void;
  onCloseDialog: () => void;
  onOrder: (p: SalesProductsOrderProps | OrderProps) => void;
  selectedOffer?: string;
  onlineModel: OnlineModel;
  price?: JSX.Element;
  subscriptionCard?: SubscriptionCardWithModel;
  newOrKeepNumber?: string;
  offers?: SubscriptionCardOffer[];
  publicPages?: boolean;
  hasQuantity?: boolean;
  isChangeOffer?: boolean;
}

export const SubscriptionModal = ({
  header,
  descriptionItems,
  onNumberSelectionChange,
  onCloseDialog,
  onOrder,
  onlineModel,
  subscriptionCard,
  newOrKeepNumber,
  offers,
  publicPages,
  hasQuantity,
  isChangeOffer,
}: SubscriptionCard2ModalProps) => {
  const { isAuthenticated } = useAuth();
  const [quantity, setQuantity] = useState<number>(1);

  return (
    <DialogWrapper size="l" closeable={true} onCloseDialog={onCloseDialog} header={header}>
      <div className="of-subscription-modal">
        <div className="of-subscription-modal-main">
          <div className="of-subscription-modal-body">
            {!isChangeOffer && (
              <div className="of-subscription-modal-number-select">
                <h4 className={dsClass.MARGIN_0}>{t.X357('Select new or current phone number ')}</h4>
                <CL.RadioGroup onChange={onNumberSelectionChange} value={newOrKeepNumber}>
                  <CL.RadioV2 value="newNumber">
                    <div>
                      <span>{t.ARFV('New number')} </span>
                      {t.ARFX('- select this option if you want your subscription to have a new number.')}
                    </div>
                  </CL.RadioV2>
                  <CL.RadioV2 value="keepNumber">
                    <div>
                      <span>{t.ARFW('Keep your number')} </span>
                      {t.ARFY(
                        '- select this option if you want to transfer your current number from another operator to Elisa.'
                      )}
                    </div>
                  </CL.RadioV2>
                </CL.RadioGroup>
              </div>
            )}
            <div className="of-subscription-modal-details-description">
              <CL.Description
                listTitle={t.NUCX('Subscription contents')}
                items={descriptionItems.map(item => ({
                  title: item.title,
                  description: item.content,
                }))}
              />
            </div>
          </div>
        </div>
        <div className="of-subscription-modal-footer ds-border--top-style-solid">
          <div className="of-subscription-modal-footer-content">
            <Price
              onetime={isChangeOffer ? subscriptionCard?.serviceFee : subscriptionCard?.oneTimePrice}
              monthly={subscriptionCard?.monthlyPrice}
              inModal={true}
              quantity={quantity}
              saving={getSaving(subscriptionCard, quantity)}
              isChangeOffer={isChangeOffer}
            />
            <div className="of-subscription-modal-footer-buttons">
              <div className="of-subscription-modal-footer-buttons-quantity">
                <div
                  className={`of-subscription-modal-footer-buttons-quantity-label ${dsClass.MARGIN_BOTTOM_1} ${dsClass.MARGIN_TOP_3} ${dsClass.HIDE_M_AND_UP}`}
                >
                  <span className={`${dsClass.TEXT_L} ${dsClass.FONT_WEIGHT_700}`}>{t.M0W7(quantityMsg)}</span>
                </div>
                {hasQuantity && (
                  <CL.Quantity
                    className={`of-subscription-quantity ${dsClass.MARGIN_RIGHT_2}`}
                    i18n_quantity_deleteAriaLabel={t.R3VE(removeMsg)}
                    i18n_quantity_minusAriaLabel={t.C2KQ(subtractMsg)}
                    i18n_quantity_plusAriaLabel={t.VKFM(addMsg)}
                    i18n_quantity_quantityAriaLabel={t.M0W7(quantityMsg)}
                    minValue={1}
                    currentValue={1}
                    onChange={(q: number) => setQuantity(q)}
                  />
                )}
              </div>
              <div className="of-subscription-modal-footer-buttons-add-to-cart">
                <CL.Button onClick={() => onCloseDialog()} color="link" className={dsClass.HIDE_M_AND_UP}>
                  {t.VLZR(continueShoppingMsg)}
                </CL.Button>
                <CL.Button
                  onClick={() => {
                    if (publicPages) {
                      onCloseDialog();
                    }
                    onOrder({
                      isAuthenticated: isAuthenticated,
                      onlineModel: onlineModel,
                      quantity: quantity,
                      isNewNumber: newOrKeepNumber === 'newNumber',
                      offers: offers || [],
                      voucherCode: subscriptionCard?.voucherCode,
                    });
                  }}
                  size="l"
                >
                  {publicPages ? t.B5J1('Add to basket') : isChangeOffer ? t.I62A('Continue') : t.AQOL(orderMsg)}
                </CL.Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DialogWrapper>
  );
};
